import { create } from 'zustand';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders } from '../lib/useQobuz';
import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  annotateRecentAlbumsWithValidation,
  type LocalRefValidationStatus,
} from '../services/localLibraryRefValidator';
import { normalizeLocalAlbumId } from '../services/localLibraryMetadata';
import { isBadCoverUrl } from '../utils/coverArt';
import { getCachedLocalAlbumCoverMeta } from '../services/albumDetailCache';
import { useMaestroStore } from '../store/maestroStore';

const LOCAL_HASH_RE = /^[0-9a-f]{16}$/i;

export interface RecentAlbum {
  album_id: string;
  source: string | null;
  title: string;
  artist: string | null;
  artwork_url: string | null;
  embedded_cover_hash?: string | null;
  folder_cover_hash?: string | null;
  quality_label: string | null;
  played_at: string;
  libraryValidationStatus?: LocalRefValidationStatus;
}

interface RecentAlbumsState {
  albums: RecentAlbum[];
  loading: boolean;
  loaded: boolean;
  fetchRecent: (force?: boolean) => Promise<void>;
  saveRecent: (album: {
    album_id: string;
    source?: string | null;
    title: string;
    artist?: string | null;
    artwork_url?: string | null;
    embedded_cover_hash?: string | null;
    folder_cover_hash?: string | null;
    quality_label?: string | null;
  }) => void;
  removeRecent: (albumId: string) => void;
  revalidateRecent: () => Promise<void>;
}

const MAX_RECENT = 25;
const LS_KEY = 'maestro_recent_albums';

/** Dedupe by album_id only — never title/artwork (singles may share cover art). */
export function normalizeRecentAlbumId(raw: string | null | undefined, source?: string | null): string {
  let id = String(raw || '').trim();
  if (!id) return '';
  id = id.replace(/^qobuz:album:/i, '');
  if (source === 'local_library' || id.startsWith('local:album:') || LOCAL_HASH_RE.test(id)) {
    id = normalizeLocalAlbumId(id);
  }
  return id;
}

const LOCAL_ALBUM_HASH_RE = /^[0-9a-f]{16}$/i;

/** Drop entries that must never be persisted (track-uri recents, missing identity). */
export function isStorableRecentAlbum(album: Pick<RecentAlbum, 'album_id' | 'source' | 'title'> | null | undefined): boolean {
  if (!album?.title?.trim()) return false;
  const raw = String(album.album_id || '').trim();
  if (!raw) return false;
  if (/^local:track:/i.test(raw)) return false;
  return normalizeRecentAlbumId(raw, album.source).length > 0;
}

export function filterStorableRecentAlbums(albums: RecentAlbum[]): RecentAlbum[] {
  return albums.filter(isStorableRecentAlbum);
}

/** UI-only: hide local recents that cannot open an album page (non-hash ids). */
export function isRenderableRecentAlbum(album: Pick<RecentAlbum, 'album_id' | 'source' | 'title'> | null | undefined): boolean {
  if (!isStorableRecentAlbum(album)) return false;
  const raw = String(album!.album_id || '').trim();
  const isLocal = album!.source === 'local_library'
    || raw.startsWith('local:album:')
    || LOCAL_ALBUM_HASH_RE.test(raw);
  if (!isLocal) return true;
  const normalized = normalizeRecentAlbumId(raw, album!.source);
  return LOCAL_ALBUM_HASH_RE.test(normalized);
}

export function filterRenderableRecentAlbums(albums: RecentAlbum[]): RecentAlbum[] {
  return albums.filter((album) => {
    if (album.libraryValidationStatus === 'missing_track') return false;
    return isRenderableRecentAlbum(album);
  });
}

type RecentTrackLike = Record<string, unknown> | null | undefined;

function readNestedAlbumId(albumValue: unknown): string | null {
  if (!albumValue || typeof albumValue !== 'object') return null;
  const album = albumValue as Record<string, unknown>;
  const raw = album.id || album.album_id || album.albumId;
  return raw != null ? String(raw).trim() : null;
}

function readNestedAlbumTitle(albumValue: unknown): string {
  if (typeof albumValue === 'string') return albumValue.trim();
  if (!albumValue || typeof albumValue !== 'object') return '';
  const album = albumValue as Record<string, unknown>;
  return String(album.title || album.name || album.albumTitle || '').trim();
}

/** Resolve album id + title from heterogeneous track payloads (Qobuz/local/radio). */
export function resolveRecentAlbumFieldsFromTrack(track: RecentTrackLike): {
  albumId: string;
  title: string;
  resolvedSource: string;
} {
  if (!track) return { albumId: '', title: '', resolvedSource: 'unknown' };

  const source = (track.source as string) || null;
  const isRadio = source === 'radio';
  const isLocal = source === 'local_library' || !!track.file_path
    || String(track.id || track.trackId || '').startsWith('local:');
  const resolvedSource = isRadio ? 'radio' : (isLocal ? 'local_library' : (source || 'qobuz'));

  const metadata = (track.metadata && typeof track.metadata === 'object')
    ? track.metadata as Record<string, unknown>
    : null;

  const albumId = normalizeRecentAlbumId(
    (track.albumId || track.album_key || track.album_id || track.stationId
      || metadata?.album_id || metadata?.albumId
      || readNestedAlbumId(track.album)
      || (isRadio ? track.id || track.trackId : undefined)) as string | undefined,
    resolvedSource,
  );

  const title = String(
    track.albumTitle || track.album_name || track.stationName
    || (typeof track.album === 'string' ? track.album : '')
    || readNestedAlbumTitle(track.album)
    || metadata?.album_title || metadata?.albumTitle
    || (isRadio ? track.title : '')
    || '',
  ).trim();

  return { albumId, title, resolvedSource };
}

export function dedupeRecentAlbumsById(albums: RecentAlbum[]): RecentAlbum[] {
  const byId = new Map<string, RecentAlbum>();
  const sorted = filterStorableRecentAlbums(albums)
    .sort((a, b) => (b.played_at || '').localeCompare(a.played_at || ''));
  for (const album of sorted) {
    const key = normalizeRecentAlbumId(album.album_id, album.source);
    if (!key) continue;
    const prev = byId.get(key);
    if (!prev || (album.played_at || '') > (prev.played_at || '')) {
      byId.set(key, { ...album, album_id: key });
    }
  }
  return [...byId.values()]
    .sort((a, b) => (b.played_at || '').localeCompare(a.played_at || ''))
    .slice(0, MAX_RECENT);
}

export function saveRecentAlbumFromTrack(track: Record<string, unknown> | null | undefined): void {
  if (!track) return;
  const { albumId, title, resolvedSource } = resolveRecentAlbumFieldsFromTrack(track);
  if (!albumId || !title) {
    return;
  }

  const rawArtUrl = (track.artworkUrl || track.image || track.artwork || null) as string | null;
  const artworkUrl = rawArtUrl && !isBadCoverUrl(rawArtUrl) ? rawArtUrl : null;

  const isLocal = resolvedSource === 'local_library';
  let embeddedHash = (track.embedded_cover_hash || track.embeddedCoverHash || null) as string | null;
  let folderHash = (track.folder_cover_hash || track.folderCoverHash || null) as string | null;
  if (isLocal && albumId && !embeddedHash && !folderHash) {
    const cached = getCachedLocalAlbumCoverMeta(albumId);
    if (cached) {
      embeddedHash = cached.embedded_cover_hash;
      folderHash = cached.folder_cover_hash;
    }
  }

  useRecentAlbumsStore.getState().saveRecent({
    album_id: albumId,
    source: resolvedSource,
    title,
    artist: String(track.artist || track.artistName || '').trim() || null,
    artwork_url: artworkUrl,
    embedded_cover_hash: embeddedHash,
    folder_cover_hash: folderHash,
  });
}

function loadFromLocalStorage(): RecentAlbum[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cleaned = dedupeRecentAlbumsById(parsed);
    if (cleaned.length !== parsed.length) {
      saveToLocalStorage(cleaned);
    }
    return cleaned;
  } catch {
    return [];
  }
}

function saveToLocalStorage(albums: RecentAlbum[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(albums.slice(0, MAX_RECENT)));
  } catch { /* quota exceeded or private mode */ }
}

function isPlaybackLive(): boolean {
  try {
    const ps = useMaestroStore.getState().playbackState;
    return ps === 'playing' || ps === 'loading';
  } catch {
    return false;
  }
}

/** Defer recents validation off the play hot path — never blocks during active playback. */
function scheduleDeferredRecentValidation(
  albums: RecentAlbum[],
  apply: (annotated: RecentAlbum[]) => void,
): void {
  const attempt = () => {
    if (isPlaybackLive()) {
      window.setTimeout(attempt, 8000);
      return;
    }
    void annotateRecentAlbumsWithValidation(albums)
      .then(apply)
      .catch((err) => {
        console.warn('[recentAlbumsStore] background validation failed:', err);
      });
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(attempt, { timeout: 60_000 });
  } else {
    window.setTimeout(attempt, 3000);
  }
}

export const useRecentAlbumsStore = create<RecentAlbumsState>((set, get) => ({
  albums: loadFromLocalStorage(),
  loading: false,
  loaded: false,

  fetchRecent: async (force = false) => {
    const state = get();
    if (state.loaded && !force) return;
    if (state.loading) return;

    set({ loading: true });
    try {
      const resp = await fetch(`${CLOUD_API_URL}/api/recent-albums`, {
        headers: getHeaders(),
        credentials: 'omit',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const serverAlbums: RecentAlbum[] = dedupeRecentAlbumsById(data.albums || []);

      const localAlbums = dedupeRecentAlbumsById(get().albums);
      const merged = dedupeRecentAlbumsById(mergeAlbumLists(serverAlbums, localAlbums));
      const paintReady = merged.map((a) => ({
        ...a,
        libraryValidationStatus: 'valid' as const,
      }));

      set({ albums: paintReady, loaded: true, loading: false });
      saveToLocalStorage(paintReady);

      scheduleDeferredRecentValidation(merged, (annotated) => {
        set({ albums: annotated });
        saveToLocalStorage(annotated);
      });
    } catch (err) {
      console.warn('[recentAlbumsStore] fetchRecent failed:', err);
      set({ loading: false, loaded: true });
    }
  },

  saveRecent: (album) => {
    const albumId = normalizeRecentAlbumId(album.album_id, album.source);
    const sourceLabel = album.source ?? 'unknown';
    if (/^local:track:/i.test(String(album.album_id || '').trim())) {
      diagnosticLog('warn', 'navigation', `[recents] save fail source=${sourceLabel} reason=track_uri`, {
        album_id: album.album_id,
      });
      return;
    }
    if (!albumId || !album.title) {
      diagnosticLog('warn', 'navigation', `[recents] save fail source=${sourceLabel} reason=missing_fields`, {
        album_id: albumId || null,
        has_title: !!album.title,
      });
      return;
    }

    diagnosticLog('info', 'navigation', `[recents] save attempt source=${sourceLabel} album=${albumId}`, {
      source: sourceLabel,
      album_id: albumId,
      has_embedded_hash: !!album.embedded_cover_hash,
      has_folder_hash: !!album.folder_cover_hash,
    });

    const entry: RecentAlbum = {
      album_id: albumId,
      source: album.source ?? null,
      title: album.title,
      artist: album.artist ?? null,
      artwork_url: album.artwork_url ?? null,
      embedded_cover_hash: album.embedded_cover_hash ?? null,
      folder_cover_hash: album.folder_cover_hash ?? null,
      quality_label: album.quality_label ?? null,
      played_at: new Date().toISOString(),
    };

    set((prev) => {
      const filtered = prev.albums.filter((a) => normalizeRecentAlbumId(a.album_id, a.source) !== albumId);
      const updated = dedupeRecentAlbumsById([entry, ...filtered]);
      saveToLocalStorage(updated);
      return { albums: updated, loaded: true };
    });

    fetch(`${CLOUD_API_URL}/api/recent-albums`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        album_id: albumId,
        source: album.source ?? null,
        title: album.title,
        artist: album.artist ?? null,
        artwork_url: album.artwork_url ?? null,
        embedded_cover_hash: album.embedded_cover_hash ?? null,
        folder_cover_hash: album.folder_cover_hash ?? null,
        quality_label: album.quality_label ?? null,
      }),
    })
      .then((resp) => {
        if (resp.ok) {
          diagnosticLog('info', 'navigation', `[recents] save ok source=${sourceLabel} album=${albumId}`, {
            source: sourceLabel,
            album_id: albumId,
          });
          return;
        }
        diagnosticLog('warn', 'navigation', `[recents] save fail source=${sourceLabel} album=${albumId}`, {
          source: sourceLabel,
          album_id: albumId,
          reason: `http_${resp.status}`,
        });
      })
      .catch((err) => {
        console.warn('[recentAlbumsStore] saveRecent API call failed:', err);
        diagnosticLog('warn', 'navigation', `[recents] save fail source=${sourceLabel} album=${albumId}`, {
          source: sourceLabel,
          album_id: albumId,
          reason: String(err?.message || err || 'network_error').slice(0, 120),
        });
      });
  },

  removeRecent: (albumId) => {
    set((prev) => {
      const updated = prev.albums.filter((a) => a.album_id !== albumId);
      saveToLocalStorage(updated);
      return { albums: updated };
    });

    fetch(`${CLOUD_API_URL}/api/recent-albums/${encodeURIComponent(albumId)}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit',
    }).catch((err) => {
      console.warn('[recentAlbumsStore] removeRecent API call failed:', err);
    });
  },

  revalidateRecent: async () => {
    const current = get().albums;
    if (!current.length) return;
    if (isPlaybackLive()) return;
    try {
      const annotated = await annotateRecentAlbumsWithValidation(current);
      set({ albums: annotated, loaded: true });
      saveToLocalStorage(annotated);
    } catch (err) {
      console.warn('[recentAlbumsStore] revalidateRecent failed:', err);
    }
  },
}));

function mergeAlbumLists(server: RecentAlbum[], local: RecentAlbum[]): RecentAlbum[] {
  return dedupeRecentAlbumsById([...server, ...local]);
}
